import { ChangeEvent, CSSProperties, MouseEvent, useContext } from 'react';

import { Checkbox, List, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { SchemaTree } from '@atom/types/schema';
import iconUtilities from '@atom/utilities/iconUtilities';

import { getQuantity, isSelected, pathKey } from './addElementsModalUtilities';
import ElementSelectionContext from './ElementSelectionContext';

import classes from './addElementsModal.module.css';

const { ListItem, ListItemButton, ListItemText } = List;

const styles = {
  listItemText: {
    marginLeft: '0.625rem',
  },
  quantityField: {
    width: '4rem',
    marginRight: '1rem',
  },
  quantityInput: {
    textAlign: 'center',
  },
};

const iconStyles = {
  imageStyle: { marginRight: '0.625rem' },
};

const getRowStyle = (depth: number, disabled: boolean): CSSProperties => ({
  boxSizing: 'border-box',
  paddingLeft: `${depth * 1.5}rem`,
  paddingRight: '6.5rem',
  cursor: disabled ? 'default' : 'pointer',
});

type ElementSelectionRowProps = {
  element: SchemaTree;
  path: string[];
  depth: number;
  disabled?: boolean;
};

const ElementSelectionRow = ({
  element,
  path,
  depth,
  disabled = false,
}: ElementSelectionRowProps) => {
  const { selection, expanded, onToggle, onQuantityChange, onToggleExpanded } =
    useContext(ElementSelectionContext);

  const key = pathKey(path);
  const isChecked = isSelected(selection, path);
  const isExpanded = expanded.has(key);
  const hasChildren = (element.elements ?? []).length > 0;

  const onExpandClick = (event: MouseEvent) => {
    event.stopPropagation();
    onToggleExpanded(key);
  };

  return (
    <ListItem
      disablePadding
      disableGutters
      secondaryAction={
        <TextField
          name={key}
          type="number"
          size="small"
          value={isChecked ? getQuantity(selection, path) : ''}
          disabled={disabled || !isChecked}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onQuantityChange(path, event.target.value)
          }
          style={styles.quantityField}
          slotProps={{ input: { style: styles.quantityInput } }}
          data-cy={`addElementQuantity-${element.name}`}
        />
      }
    >
      <ListItemButton
        disableGutters
        disableRipple={disabled}
        style={getRowStyle(depth, disabled)}
        onClick={disabled ? undefined : () => onToggle(element, path)}
        data-cy={`addElementRow-${element.name}`}
      >
        {hasChildren ? (
          <span
            className={classes.expandToggle}
            onClick={onExpandClick}
            data-cy={`addElementExpand-${element.name}`}
          >
            {iconUtilities.leftIcon(isExpanded ? 'down-arrow' : 'right-arrow')}
          </span>
        ) : (
          <span className={classes.expandToggle} />
        )}
        <Checkbox
          checked={isChecked}
          disabled={disabled}
          data-cy={`addElementCheckbox-${element.name}`}
        />
        {iconUtilities.buildElementIcon(element.markerId, iconStyles, false)}
        <ListItemText
          primary={element.name}
          primaryTextStyle={{
            ...styles.listItemText,
            ...(disabled ? { color: colors.neutral.gray } : {}),
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

export default ElementSelectionRow;
